import { useLocation } from "react-router-dom";
import { useOverwriteRequest } from "../hooks";
import React from "react";
import { Box, Button, Chip, Link, Stack, Typography } from "@mui/material";
import {
  CustomeDialog as CustomDialog,
  CustomTable,
  DebouncedSearch,
} from "../../../shared";
import { PlAndBsListFilterSection } from "./PlAndBsListFilterSection";

export const OverwriteRequests = () => {
  const location = useLocation();
  const isStoreAdmin = location.pathname.includes("/store-admin");

  const {
    state,
    handleClearFilter,
    handleClearAllFilter,
    handleApplyFilter,
    handleSearch,
    downloadUploadedFile,
    handleChangeFilter,
    handleOpenOrCloseAlert,
    handleRejectOrApprove,
  } = useOverwriteRequest();

  const requestColumns = React.useMemo(
    () => [
      {
        Header: "Submission Date",
        accessor: "last_updated_on",
      },
      {
        Header: "Store",
        accessor: "store_name",
      },
      {
        Header: "Month ",
        accessor: "month",
      },
      {
        Header: "Financial Year",
        accessor: "year",
      },
      {
        Header: "File",
        accessor: "excel_file",
        Cell: ({ row }) => {
          return (
            <Box
              sx={{
                textDecoration: "none",
                cursor: "pointer",
                color: "info.main",
              }}
            >
              <Link
                underline="none"
                component="button"
                variant="body2"
                onClick={() => downloadUploadedFile(row?.original?.id)}
              >
                <b>Download</b>
              </Link>
            </Box>
          );
        },
      },
      {
        Header: "Submitted By",
        accessor: "user_name",
      },
      {
        Header: "Action",
        id: "action",
        align: "right",
        disableSortBy: true,
        disableGlobalFilter: true,
        accessor: (row) => {
          return (
            <React.Fragment>
              {row.can_modify === 3 ? ( // 3 for rejected status
                <Chip label="Rejected" />
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    handleOpenOrCloseAlert({
                      open: true,
                      accountUploadId: row.id,
                    });
                  }}
                >
                  Approve/Reject
                </Button>
              )}
            </React.Fragment>
          );
        },
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <CustomTable
        key="overwriteRequest"
        defaultSearch={false}
        columns={requestColumns}
        data={state.accountOverwriteList}
        isLoading={state.tableLoading}
        filterContent={
          <DebouncedSearch value={state.searchText} onChange={handleSearch} />
        }
        filterSection={
          <Stack
            width="100%"
            gap="16px"
            flexDirection="row"
            justifyContent="space-between"
          >
            <Stack
              flexDirection="row"
              gap="16px"
              flexGrow={1}
              maxWidth={{ md: "70%", xs: "100%" }}
            >
              <PlAndBsListFilterSection
                filter={state.filter}
                accountMonthList={true}
                financialYearList={true}
                storeList={!isStoreAdmin}
                handleChangeFilter={handleChangeFilter}
              />
            </Stack>
            <Button
              size="large"
              variant="contained"
              onClick={() => handleApplyFilter(state.filter.values)}
              disabled={state.filter.isFilterApplied}
            >
              Apply
            </Button>
          </Stack>
        }
        filterApplied={state.filter.appliedFilters}
        onAllFilterClear={handleClearAllFilter}
        onFilterClear={handleClearFilter}
      />
      <CustomDialog
        open={state.isRejectOrApprovalDialogOpen}
        handleClose={() => handleOpenOrCloseAlert({ open: false })}
        PaperProps={{
          sx: {
            width: "423px",
            height: "167px",
            p: "24px 24px 16px 24px",
            gap: "12px",
          },
        }}
        title="Account File Overwrite Request"
        content={
          <Stack flexDirection="column" justifyContent="space-between">
            <Typography fontSize="16px" fontWeight={400} lineHeight="19.36px">
              Would you like to approve or reject the Account File Overwrite
              request ?
            </Typography>
            <Stack flexDirection="row" gap="12px" justifyContent="end">
              <Button
                size="small"
                variant="text"
                color="error"
                onClick={() => handleRejectOrApprove({ action: "reject" })}
              >
                Reject
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => handleRejectOrApprove({ action: "approve" })}
              >
                Approve
              </Button>
            </Stack>
          </Stack>
        }
      />
    </React.Fragment>
  );
};
